<template>
	<div class="author-card-sidebar">
		<!-- Show loading skeleton only on client-side navigation, not during SSR -->
		<ClientOnly>
			<div
				class="flex flex-col gap-3"
				v-if="loading"
			>
				<span class="pulse bg-white/10 !rounded-full w-[130px] min-h-[130px]" />
				<span class="pulse bg-white/10 !rounded-md w-full min-h-[20px]" />
				<span class="pulse bg-white/10 !rounded-md w-full h-[60px]" />
				<span class="pulse bg-white/10 !rounded-md w-full h-[20px]" />
			</div>
		</ClientOnly>

		<template v-if="!loading">
			<img
				:src="authorInfo.image || '/images/author-card.webp'"
				width="130"
				height="130"
				alt="Author card Timothy Sykes picture"
				class="mb-3 rounded-full object-cover h-32 w-32"
				:class="{ 'border-[3px] border-[#91c4fc]': authorInfo.image }"
				loading="lazy"
			/>
			<h4 v-html="authorInfo.name ? authorInfo.name : 'Timothy Sykes'" />

			<Paragraph>
				<div
					class="body flex flex-col gap-2"
					v-html="
						authorInfo.bio ||
						`Tim Sykes is a penny stock trader and teacher who became a self-made
			millionaire by the age of 22 by trading $12,415 of bar mitzvah money. After
			becoming disenchanted with the hedge fund world, he established the Tim Sykes
			Trading Challenge to teach aspiring traders how to follow his trading
			strategies. He’s been featured in a variety of media outlets including CNN,
			Larry King, Steve Harvey, Forbes, Men’s Journal, and more. He’s also an
			active philanthropist and environmental activist, a co-founder of Karmagawa,
			and has donated millions of dollars to charity.`
					"
				></div>

				<NuxtLink
					class="read-more"
					:to="biographyLink"
				>
					Read More
					<svg
						width="14"
						height="13"
						viewBox="0 0 14 13"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<line
							x1="12"
							y1="6.5"
							y2="6.5"
							stroke="#FDE047"
							stroke-width="2"
						/>
						<path
							d="M8.33198 0.5L7 1.91L11.3266 6.5L7 11.09L8.33198 12.5L14 6.5L8.33198 0.5Z"
							fill="#FDE047"
						/>
					</svg>
				</NuxtLink>
			</Paragraph>
		</template>
	</div>
</template>

<script setup>
import { load } from "cheerio";

const props = defineProps({
	authorName: {
		type: String,
		required: false,
		default: "Timothy Sykes"
	}
});

// Default author info for Timothy Sykes
const defaultAuthorInfo = {
	name: null,
	image: null,
	bio: null
};

const { data, pending } = await useAsyncData(
	`author-biography-details-${props.authorName}`,
	async () => {
		try {
			// For Timothy Sykes, return default values (not null)
			if (props.authorName === "Timothy Sykes" || !props.authorName) {
				return { ...defaultAuthorInfo, isDefault: true };
			}

			const author = await fetchAuthorByName(
				"https://content.timothysykes.com/",
				props.authorName.split(" ").join("-").toLowerCase()
			);

			if (!author) {
				return { ...defaultAuthorInfo, isDefault: true };
			}

			// Parse highlights on server side to avoid hydration mismatch
			let bio = null;
			if (author.highlights) {
				const $ = load(author.highlights, {
					decodeEntities: false,
					xmlMode: false
				});

				const highlights = [];
				$("li").each((_, element) => {
					highlights.push($(element).text());
				});

				bio = author.position + "<div/> " + highlights.join("\n");
			}

			return {
				name: author.name,
				image: author.profile_image,
				bio,
				isDefault: false
			};
		} catch (error) {
			// Return default values instead of undefined to prevent hydration issues
			return { ...defaultAuthorInfo, isDefault: true };
		}
	}
);

// Use computed to derive authorInfo from async data - consistent between SSR and client
const authorInfo = computed(() => {
	if (data.value && !data.value.isDefault) {
		return {
			name: data.value.name,
			image: data.value.image,
			bio: data.value.bio
		};
	}
	return defaultAuthorInfo;
});

// Don't use pending for loading state during SSR - it causes hydration mismatch
// During SSR, always return false (show content)
// On client, check if data is not yet available
const loading = computed(() => {
	// On server, never show loading state
	if (!process.client) return false;
	// On client, show loading if data is not available
	return !data.value;
});

const biographyLink = computed(() => {
	const name = authorInfo.value.name;
	if (name) {
		if (name === "Tim Sykes") {
			return "/blog/author/tim-sykes/";
		} else {
			return `/blog/author/${name.split(" ").join("-").toLowerCase()}/`;
		}
	}

	return "/blog/author/tim-sykes/";
});
</script>

<style lang="scss">
@import "@assets/utils.scss";

.author-card-sidebar h4 {
	color: #fff;
	font-family: Fira Sans;
	font-size: 24px;
	font-style: normal;
	font-weight: 700;
	line-height: 120%;
	margin-bottom: 8px;
}

.author-card-sidebar p {
	color: #fff;
	font-family: Rubik;
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 150%;
}

.read-more {
	color: #fde047;
	font-family: Fira Sans;
	font-size: 18px;
	font-style: normal;
	font-weight: 600;
	line-height: 25px;
	/* 138.889% */
	display: flex;
	align-items: center;
	gap: 8px;
}

.author-card-sidebar {
	padding: 24px 32px;
	background-color: $primary-900;
	border-radius: 16px;
}

.author-card-sidebar .body a {
	color: $primary-300;
	&:hover {
		text-decoration: underline;
	}
}
</style>
